// Shared vocabulary for tracking-update statuses. Each consumer decides
// its own policy (which statuses to include/exclude) — this module only
// owns normalization and the canonical "stop activity" status set so the
// rule stays consistent across authenticated and public tracking views.

/**
 * Tracking-event statuses that represent activity at a stop (loading/unloading
 * at pickup or delivery). Map views typically hide these to avoid stacking
 * pings on top of the stop markers that already convey the same information.
 *
 * IMPORTANT: this is a closed set, not an inference. Any new backend status
 * value that should be treated as stop activity must be added here explicitly
 * — an unrecognized status silently falls through to the "Tendered" default
 * in {@link getTrackingStep} rather than raising an error.
 */
export const STOP_ACTIVITY_STATUSES: ReadonlySet<string> = new Set([
	"waitingtoload",
	"loaded",
	"waitingtounload",
	"unloaded",
	"delivered",
]);

/**
 * Statuses that represent a shipment actively moving between pickup and
 * delivery (picked up, but not yet delivered).
 *
 * IMPORTANT: this is a closed set, not an inference. Any new backend status
 * value that should be treated as "in transit" must be added here explicitly
 * — an unrecognized status silently falls through to the "Tendered" default
 * in {@link getTrackingStep} rather than raising an error. If the API's
 * status vocabulary changes, update this set to match.
 */
export const IN_TRANSIT_STATUSES: ReadonlySet<string> = new Set([
	"intransit",
	"pickedup",
	"notdelivered",
	"waitingtoload",
	"loaded",
	"waitingtounload",
	"unloaded",
]);

/**
 * High-level tracking progress steps shared by the logged-in load tracking
 * drawer and the public shipment tracking page. A single top-level shipment
 * `status` string is collapsed into one of these three steps.
 */
export const TRACKING_STEPS = ["Tendered", "In Transit", "Delivered"] as const;
export type TrackingStep = (typeof TRACKING_STEPS)[number];

/**
 * Normalizes a status string for case- and separator-insensitive comparison.
 * Returns an empty string for null/undefined input.
 */
export function normalizeStatus(status: string | null | undefined): string {
	return (status ?? "")
		.trim()
		.toLowerCase()
		.replaceAll(/[\s_-]/gu, "");
}

/**
 * Returns true if the given status represents stop activity (see
 * {@link STOP_ACTIVITY_STATUSES}). Handles arbitrary casing and separators.
 */
export function isStopActivityStatus(
	status: string | null | undefined,
): boolean {
	return STOP_ACTIVITY_STATUSES.has(normalizeStatus(status));
}

/**
 * Formats a raw distance string (e.g. from a tracking event's `distance`
 * field) for display as "Distance to next stop". Returns `null` for
 * missing/placeholder values, or for any value that doesn't parse as a
 * finite number, so callers can omit the field entirely rather than
 * rendering a malformed API value (e.g. "NaN" or an error string) verbatim.
 */
export function formatDistanceToNextStop(
	distance: string | undefined,
): string | null {
	if (distance == null || distance === "" || distance === "-") {
		return null;
	}
	const numeric = Number(distance);
	if (Number.isFinite(numeric)) {
		return `${distance} miles`;
	}
	return null;
}

/**
 * Collapses a shipment's top-level `status` string into one of the three
 * {@link TRACKING_STEPS}. This is the single source of truth for "has this
 * shipment been picked up / delivered yet" — there is no reliable per-stop
 * "actual" flag available on the public tracking API (an earlier `isLoaded`
 * per-stop flag was intentionally removed for data privacy reasons), so both
 * the logged-in drawer and the public tracking page derive pickup/delivery
 * state from this single top-level status.
 */
export function getTrackingStep(
	status: string | null | undefined,
): TrackingStep {
	const normalized = normalizeStatus(status);
	if (!normalized) {
		return "Tendered";
	}

	if (normalized === "delivered") {
		return "Delivered";
	}
	if (IN_TRANSIT_STATUSES.has(normalized)) {
		return "In Transit";
	}

	return "Tendered";
}
